"use client";
import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import { Sora, Newsreader, DM_Sans, DM_Mono, DM_Serif_Display } from "next/font/google";
import { SignUpButton, useAuth, useOrganization } from "@clerk/nextjs";
import { useRouter } from "next/navigation";
import MobileLanding from "./MobileLanding";
import CookieConsent from "./CookieConsent";
import LandingNav from "@/app/(marketing)/_components/landing-nav";
import LandingFooter from "@/components/LandingFooter";
import HeroSequence from "@/app/(marketing)/_components/HeroSequence";

// Fonts loaded via next/font so they are self-hosted, preloaded, and NON
// render-blocking. This replaces the synchronous @import url(fonts.googleapis…)
// that previously sat inside a <style> tag and delayed FCP/LCP. Mirrors the
// approach already used in MobileLanding.tsx. Each font exposes a CSS variable
// that the landing markup references via var(--font-*).
const landingSans = DM_Sans({
  subsets: ["latin"],
  weight: ["400", "500", "600", "700"],
  display: "swap",
  variable: "--font-sans",
});
const landingDisplay = Sora({
  subsets: ["latin"],
  weight: ["300", "400", "600", "700", "800"],
  display: "swap",
  variable: "--font-display",
});
const landingSerif = Newsreader({
  subsets: ["latin"],
  weight: ["400", "500", "600", "700"],
  style: ["normal", "italic"],
  display: "swap",
  variable: "--font-serif",
});
const landingMono = DM_Mono({
  subsets: ["latin"],
  weight: ["400", "500"],
  style: ["normal", "italic"],
  display: "swap",
  variable: "--font-mono",
});
const landingSerifDisplay = DM_Serif_Display({
  subsets: ["latin"],
  weight: "400",
  style: ["normal", "italic"],
  display: "swap",
  variable: "--font-serif-display",
});
const landingFontClass = `${landingSans.variable} ${landingDisplay.variable} ${landingSerif.variable} ${landingMono.variable} ${landingSerifDisplay.variable}`;

// Below this width we render the dedicated MobileLanding page instead of the
// desktop landing. Keeps the desktop experience completely untouched.
const MOBILE_BREAKPOINT = 768;
export default function SvenetidLanding({
  forceLocale,
  initialIsMobile,
}: { forceLocale?: 'da' | 'en'; initialIsMobile?: boolean } = {}) {
  const [illLang] = useState<'da'|'en'>(forceLocale ?? 'da');
  const illStageRef = useRef<HTMLDivElement>(null);
  const illCanvasRef = useRef<HTMLDivElement>(null);
  const svendStageRef = useRef<HTMLDivElement>(null);
  const svCardEls = useRef<(HTMLDivElement | null)[]>([]);
  const svCardRef = (i: number) => (el: HTMLDivElement | null) => { svCardEls.current[i] = el; };

  // Signed-in users should never sit on the marketing page (e.g. after a
  // session-timeout bounce where the browser still holds a valid token).
  // LandingNav has the same redirect, but it never mounts on mobile viewports
  // (MobileLanding is returned early below), so it must also live here.
  const { isSignedIn } = useAuth();
  const { organization } = useOrganization();
  const router = useRouter();
  useEffect(() => {
    if (isSignedIn && organization) {
      router.push(`/organization/${organization.id}`);
    } else if (isSignedIn) {
      router.push("/select-org");
    }
  }, [isSignedIn, organization, router]);

  // Mobile detection. Seeded from the server's User-Agent sniff (see
  // middleware.ts + app/page.tsx) so the correct variant is present in the
  // very first server-rendered HTML — important for mobile-first indexing,
  // since Googlebot Smartphone would otherwise see desktop markup until a
  // client-side matchMedia check ran after hydration. Still re-checked on
  // the client so resizing/rotating the viewport updates the layout live.
  const [isMobile, setIsMobile] = useState<boolean>(initialIsMobile ?? false);
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    const update = () => setIsMobile(mql.matches);
    update();
    mql.addEventListener("change", update);
    return () => mql.removeEventListener("change", update);
  }, []);

  // When rendered at /en, prefix internal page links with /en (anchors unchanged).
  const isEn = illLang === 'en';
  const localized = (path: string) =>
    path.startsWith('#') ? path : isEn ? `/en${path === '/' ? '' : path}` : path;

  // Keep the app-wide locale cookie in sync with this page's language, so other
  // cookie-driven pages (pricing, privacy, …) stay consistent after navigating
  // between the Danish (/) and English (/en) landing pages.
  useEffect(() => {
    try {
      const target = forceLocale ?? 'da';
      localStorage.setItem('locale', target);
      const expires = new Date(Date.now() + 365 * 864e5).toUTCString();
      document.cookie = `locale=${target}; expires=${expires}; path=/; SameSite=Lax`;
      document.documentElement.lang = target;
      window.dispatchEvent(new CustomEvent('localeChange', { detail: target }));
    } catch {
      /* no-op */
    }
  }, [forceLocale]);



  const [desktopView, setDesktopView] = useState(0);
  const desktopTabs = illLang === 'da'
    ? ['Oversigt', 'Kalender', 'Rapporter']
    : ['Overview', 'Calendar', 'Reports'];
  const desktopShots = ['/overview.png', '/Calendar.png', '/Report.png'];

  useEffect(() => {
    function fitIll() {
      if (!illStageRef.current || !illCanvasRef.current) return;
      const s = illStageRef.current.offsetWidth / 1600;
      illStageRef.current.style.height = 900 * s + 'px';
      illCanvasRef.current.style.transform = `scale(${s})`;
    }
    fitIll();
    window.addEventListener('resize', fitIll);
    return () => window.removeEventListener('resize', fitIll);
  }, []);

  // "Svend AI" section: cards fly in from the corners and converge onto the
  // phone as the section scrolls into the middle of the viewport.
  useEffect(() => {
    const cards = svCardEls.current
      .filter((el): el is HTMLDivElement => !!el)
      .map((el) => ({
        el,
        dx: parseFloat(el.getAttribute('data-dx') || '0') || 0,
        dy: parseFloat(el.getAttribute('data-dy') || '0') || 0,
        rot: parseFloat(el.getAttribute('data-rot') || '0') || 0,
        delay: parseFloat(el.getAttribute('data-delay') || '0') || 0,
      }));
    if (!cards.length) return;

    const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let ticking = false;

    const update = () => {
      ticking = false;
      const stage = svendStageRef.current;
      if (!stage) return;
      const rect = stage.getBoundingClientRect();
      const vh = window.innerHeight;
      const denom = vh / 2 + rect.height / 2;
      let p = (vh - rect.top) / (denom || 1);
      p = Math.max(0, Math.min(1, p));
      if (reduced) p = 1;
      for (const c of cards) {
        let e = (p - c.delay) / (1 - c.delay);
        e = Math.max(0, Math.min(1, e));
        e = e * e * (3 - 2 * e);
        const inv = 1 - e;
        const tx = c.dx * inv;
        const ty = c.dy * inv;
        const r = c.rot * inv;
        const scale = 1 - 0.5 * e;
        const op = e < 0.72 ? 1 : Math.max(0, 1 - (e - 0.72) / 0.24);
        c.el.style.transform = `translate(${tx}px,${ty}px) rotate(${r}deg) scale(${scale})`;
        c.el.style.opacity = String(op);
      }
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  // On mobile viewports, render the dedicated mobile landing page.
  if (isMobile) {
    return (
      <>
        <MobileLanding lang={illLang} />
        <CookieConsent lang={illLang} />
      </>
    );
  }

  return (
    <div
      className={landingFontClass}
      style={{
        fontFamily: "var(--font-display), var(--font-sans), sans-serif",
        background: "#FFFFFF",
        color: "#1A1A1A",
        overflowX: "hidden",
      }}
    >
      <style>{`
        /* Fonts are loaded via next/font (see top of this file); the former
           render-blocking font import has been removed. The --font-* CSS
           variables below are provided by next/font on the wrapper div. */

        * { box-sizing: border-box; margin: 0; padding: 0; }

        /* Landing page: footer is dark; override the app's light body bg so no
           light bar shows below the footer (incl. overscroll/rubber-band). */
        body { background-color: #0F1513 !important; }

        .footer-link:hover { color: #EEF4F0 !important; }

        :root {
          --green-dark: #1B4332;
          --blue-dark: #0000FF;
          --green-mid: #2D6A4F;
          --green-light: #52B788;
          --green-pale: #D8F3DC;
          --gold: #D4A017;
          --cream: #F4F1EB;
          --ink: #1A1A1A;
          --muted: #6B6B6B;
          --border: #D9D5CC;
        }

        html { scroll-behavior: smooth; }

        .nav-link {
          color: var(--ink);
          text-decoration: none;
          font-size: 14px;
          font-weight: 500;
          opacity: 0.7;
          transition: opacity 0.2s;
        }
        .nav-link:hover { opacity: 1; }

        .btn-primary {
          background: var(--blue-dark);
          color: white;
          border: none;
          padding: 14px 28px;
          border-radius: 6px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          font-family: inherit;
          letter-spacing: -0.01em;
        }
        .btn-primary:hover {
          background: #0000cc;
          transform: translateY(-1px);
          box-shadow: 0 8px 24px rgba(0,0,200,0.25);
        }

        .btn-outline {
          background: transparent;
          color: var(--green-dark);
          border: 2px solid var(--green-dark);
          padding: 13px 28px;
          border-radius: 6px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          font-family: inherit;
        }
        .btn-outline:hover {
          background: var(--green-dark);
          color: white;
        }

        .feature-card {
          background: white;
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 28px;
          transition: all 0.3s;
          cursor: pointer;
        }
        .feature-card:hover, .feature-card.active {
          border-color: var(--green-light);
          box-shadow: 0 12px 40px rgba(27,67,50,0.12);
          transform: translateY(-3px);
        }

        .trade-badge {
          display: inline-block;
          padding: 6px 14px;
          border-radius: 100px;
          font-size: 13px;
          font-weight: 600;
          border: 1.5px solid var(--border);
          background: white;
          transition: all 0.3s;
        }
        .trade-badge.active {
          background: var(--green-dark);
          color: white;
          border-color: var(--green-dark);
        }

        .price-card {
          background: white;
          border: 2px solid var(--border);
          border-radius: 16px;
          padding: 40px 36px;
          transition: all 0.3s;
        }
        .price-card.featured {
          background: var(--green-dark);
          color: white;
          border-color: var(--green-dark);
          transform: scale(1.04);
        }

        .check-item {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 14px;
          padding: 6px 0;
        }
        .check-icon {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--green-pale);
          color: var(--green-dark);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          flex-shrink: 0;
          font-weight: 700;
        }
        .price-card.featured .check-icon {
          background: rgba(255,255,255,0.2);
          color: white;
        }

        .stat-box {
          text-align: center;
          padding: 32px 24px;
          border-right: 1px solid var(--border);
        }
        .stat-box:last-child { border-right: none; }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .svend-badge-wrap {
          display: inline-flex; position: relative;
          border-radius: 100px;
          margin-bottom: 28px;
        }
        .svend-badge {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 6px 14px;
          border-radius: 100px; font-size: 13px; font-weight: 600;
          color: var(--green-dark);
          border: 1.5px solid var(--border);
          background: #fff;
        }
        .fade-up {
          animation: fadeUp 0.7s ease forwards;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes ticker {
          0%, 20% { transform: translateY(0); opacity: 1; }
          25%, 95% { transform: translateY(-20px); opacity: 0; }
          100% { transform: translateY(0); opacity: 1; }
        }

        @keyframes pulse-ring {
          0% { transform: scale(1); opacity: 0.4; }
          100% { transform: scale(1.6); opacity: 0; }
        }

        .cta-glow-btn {
          position: relative;
          overflow: hidden;
          isolation: isolate;
        }
        .cta-glow-btn::after {
          content: "";
          position: absolute;
          inset: 0;
          border-radius: inherit;
          background: linear-gradient(
            100deg,
            transparent 20%,
            rgba(216, 70, 160, 0.75) 46%,
            rgba(168, 85, 247, 0.85) 50%,
            rgba(216, 70, 160, 0.75) 54%,
            transparent 80%
          );
          background-size: 250% 100%;
          background-position: 150% 0;
          mix-blend-mode: screen;
          pointer-events: none;
          animation: cta-sweep 5s ease-in-out infinite;
          z-index: 1;
        }
        @keyframes cta-sweep {
          0% { background-position: 150% 0; }
          40%, 100% { background-position: -50% 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          .cta-glow-btn::after { animation: none; display: none; }
        }

        .screenshot-mock {
          background: white;
          border-radius: 12px;
          border: 1px solid var(--border);
          overflow: hidden;
          box-shadow: 0 24px 80px rgba(0,0,0,0.1);
        }

        .nav-scrolled {
          box-shadow: 0 2px 20px rgba(0,0,0,0.08);
        }

        /* Dashboard tab button */
        .dash-tab {
          padding: 6px 14px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          border: 1.5px solid transparent;
          background: transparent;
          color: var(--muted);
          font-family: inherit;
          transition: all 0.2s;
        }
        .dash-tab:hover { color: var(--ink); }
        .dash-tab.active {
          background: white;
          border-color: var(--border);
          color: var(--green-dark);
          box-shadow: 0 2px 8px rgba(0,0,0,0.06);
        }

        /* Dashboard screenshot transition */
        .dash-img-wrap {
          position: relative;
          width: 100%;
          overflow: hidden;
          border-radius: 0 0 10px 10px;
        }
        .dash-img-wrap img {
          width: 100%;
          display: block;
          animation: fadeIn 0.4s ease;
        }

        /* Phone frame (reduced size) */
        .phone-frame {
          background: #1C1C1E;
          border-radius: 36px;
          padding: 8px 8px;
          box-shadow: 0 28px 70px rgba(0,0,0,0.28), inset 0 0 0 1px rgba(255,255,255,0.12);
          position: relative;
        }
        .phone-frame::before {
          content: '';
          position: absolute;
          top: 12px;
          left: 50%;
          transform: translateX(-50%);
          width: 22px;
          height: 6px;
          background: #000;
          border-radius: 8px;
          z-index: 10;
        }
        .phone-inner {
          border-radius: 28px;
          overflow: hidden;
          background: white;
          position: relative;
        }
        .phone-inner img {
          width: 100%;
          display: block;
        }

        /* Platform badges */
        .platform-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          border-radius: 100px;
          font-size: 13px;
          font-weight: 600;
          background: white;
          border: 1.5px solid var(--border);
          color: var(--ink);
        }

        /* Features dropdown — redesigned as integrated mega-menu */
        .features-dropdown {
          position: fixed;
          top: 64px;
          left: 0;
          right: 0;
          z-index: 95;
          background: rgba(255,255,255,0.97);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border-bottom: 1px solid var(--border);
          box-shadow: 0 16px 48px rgba(0,0,0,0.1);
          transform-origin: top center;
          transition: opacity 0.2s ease, transform 0.2s ease;
        }
        .features-dropdown.open {
          opacity: 1;
          transform: scaleY(1);
          pointer-events: all;
        }
        .features-dropdown.closed {
          opacity: 0;
          transform: scaleY(0.96);
          pointer-events: none;
        }

        .dropdown-feature-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 12px;
          border-radius: 10px;
          text-decoration: none;
          color: var(--ink);
          transition: background 0.15s;
          cursor: pointer;
        }
        .dropdown-feature-item:hover {
          background: rgba(0,0,0,0.04);
        }
        .dropdown-feature-item:hover .dropdown-icon {
          color: var(--green-dark);
        }
        .dropdown-icon {
          width: 28px;
          height: 28px;
          background: transparent;
          color: var(--muted);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 19px;
          line-height: 1;
          flex-shrink: 0;
          transition: color 0.15s;
        }

        /* Hero: fix landscape/short viewport spacing */
        .hero {
          padding-top: calc(64px + 80px);
          padding-bottom: 80px;
          padding-left: clamp(12px, 2vw, 24px);
          padding-right: clamp(12px, 2vw, 24px);
          width: 100%;
          max-width: 1280px;
          margin-left: auto;
          margin-right: auto;
        }

        /* ======== RESPONSIVE ======== */

        @media (max-width: 1100px) {
          .hero-dashboard {
            min-height: 400px !important;
          }
        }

        /* Tablet */
        @media (max-width: 900px) {
          .hero {
            padding-left: 64px;
            padding-right: 64px;
            padding-top: calc(64px + 32px);
          }
          .hero-dashboard {
            display: none !important;
          }
          .hero-text {
            max-width: 100% !important;
          }
          .nav-center { display: none !important; }
          .nav-mobile-menu { display: flex !important; }
          .features-grid-3 {
            grid-template-columns: 1fr 1fr !important;
          }
          .how-grid-4 {
            grid-template-columns: 1fr 1fr !important;
          }
          .pricing-grid {
            grid-template-columns: 1fr !important;
          }
          .price-card.featured {
            transform: none !important;
          }
          .footer-grid {
            grid-template-columns: 1fr 1fr !important;
          }
          .app-section-grid {
            grid-template-columns: 1fr !important;
          }
          .phones-row {
            justify-content: center !important;
          }
          .section-pad {
            padding-left: 24px !important;
            padding-right: 24px !important;
          }
        }

        /* Mobile */
        @media (max-width: 600px) {
          .hero {
            padding-top: calc(64px + 24px);
            padding-left: 20px;
            padding-right: 20px;
            padding-bottom: 48px;
          }
          .hero h1 {
            font-size: clamp(30px, 9vw, 44px) !important;
          }
          .hero p {
            font-size: 16px !important;
          }
          .hero-cta-row {
            flex-direction: column !important;
            align-items: stretch !important;
          }
          .hero-cta-row button, .hero-cta-row a {
            width: 100%;
            text-align: center;
            justify-content: center;
          }
          .features-grid-3 {
            grid-template-columns: 1fr !important;
          }
          .how-grid-4 {
            grid-template-columns: 1fr !important;
          }
          .footer-grid {
            grid-template-columns: 1fr !important;
          }
          .cta-btn-row {
            flex-direction: column !important;
            gap: 12px !important;
          }
          .cta-btn-row button {
            width: 100%;
          }
          .section-pad {
            padding-left: 20px !important;
            padding-right: 20px !important;
            padding-top: 64px !important;
            padding-bottom: 64px !important;
          }
          .stat-grid {
            grid-template-columns: 1fr 1fr !important;
          }
          .stat-box {
            border-right: none !important;
            border-bottom: 1px solid var(--border);
          }
          .phones-row {
            flex-direction: column !important;
            align-items: center !important;
            gap: 24px !important;
          }
          .phone-frame {
            width: 240px !important;
          }
        }

        /* Landscape / short viewports — reduce hero top spacing */
        @media (orientation: landscape) and (max-height: 700px) {
          .hero {
            padding-top: calc(64px + 16px) !important;
            padding-bottom: 32px !important;
          }
        }

        /* Nav mobile */
        .nav-mobile-menu { display: none; }
        .mobile-menu-panel {
          position: fixed;
          top: 64px;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(244,241,235,0.98);
          backdrop-filter: blur(12px);
          z-index: 90;
          padding: 24px;
          display: flex;
          flex-direction: column;
          gap: 8px;
          overflow-y: auto;
        }
        .mobile-nav-link {
          display: block;
          padding: 14px 0;
          font-size: 20px;
          font-weight: 600;
          color: var(--ink);
          text-decoration: none;
          border-bottom: 1px solid var(--border);
        }

        /* App store badges */
        .store-badge {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 10px 20px;
          border-radius: 12px;
          background: #111;
          color: white;
          font-size: 13px;
          font-weight: 600;
          font-family: inherit;
          border: none;
          cursor: pointer;
          transition: all 0.2s;
        }
        .store-badge:hover {
          background: #333;
          transform: translateY(-2px);
        }
        .store-badge svg { flex-shrink: 0; }

  .st-section {
    background: #F4F1EB;
    padding: 80px clamp(8px, calc(25.2vw - 203px), 160px);
    font-family: var(--font-sans), sans-serif;
    color: #1a1a18;
    box-sizing: border-box;
    overflow: hidden;
  }

  .st-eyebrow {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.12em;
    color: #3B6D11;
    text-transform: uppercase;
    margin-bottom: 14px;
  }

  .st-heading {
    font-family: var(--font-serif-display), serif;
    font-size: clamp(32px, 4.5vw, 52px);
    line-height: 1.08;
    letter-spacing: -0.02em;
    margin: 0 0 18px;
    color: #1a1a18;
  }

  .st-heading em {
    font-style: italic;
    color: #3B6D11;
  }

  .st-body {
    font-size: 16px;
    color: #5a5a52;
    line-height: 1.65;
    max-width: 420px;
    margin: 0 0 32px;
  }

  .st-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 56px;
    align-items: center;
    max-width: 1100px;
    margin: 0 auto;
  }

  .st-features {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .st-feature {
    display: flex;
    gap: 16px;
    padding: 18px 0;
    border-bottom: 1px solid rgba(0,0,0,0.08);
    align-items: flex-start;
  }

  .st-feature:last-child { border-bottom: none; }

  .st-icon {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    background: #3B6D11;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-top: 2px;
  }

  .st-feature-title {
    font-size: 15px;
    font-weight: 600;
    color: #1a1a18;
    margin: 0 0 3px;
  }

  .st-feature-desc {
    font-size: 13.5px;
    color: #6b6b62;
    margin: 0;
    line-height: 1.55;
  }

  .st-badges {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 40px;
  }

  .st-badge {
    display: flex;
    align-items: center;
    gap: 7px;
    background: #1a1a18;
    color: white;
    border-radius: 10px;
    padding: 8px 14px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
  }

  .st-badge-label {
    display: flex;
    flex-direction: column;
  }

  .st-badge-sub {
    font-size: 9px;
    opacity: 0.55;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    line-height: 1;
  }

  .st-badge-name {
    font-size: 13px;
    font-weight: 600;
    line-height: 1.2;
  }

  /* Phone mockup */
  .phones-wrap {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding: 20px 0 40px;
    min-height: 460px;
  }

  .phone-shell {
    position: relative;
    width: 200px;
    background: #0f0f0f;
    border-radius: 36px;
    padding: 8px;
    box-shadow:
      inset 0 0 0 1px #3a3a3a,
      0 24px 60px rgba(0,0,0,0.26),
      0 6px 18px rgba(0,0,0,0.14);
  }

  .phone-notch {
    position: absolute;
    top: 14px;
    left: 50%;
    transform: translateX(-50%);
    width: 56px;
    height: 18px;
    background: #0f0f0f;
    border-radius: 0 0 14px 14px;
    z-index: 10;
  }

  .phone-screen {
    width: 100%;
    border-radius: 26px;
    overflow: hidden;
    aspect-ratio: 9/19.5;
    background: #f0eeea;
    position: relative;
  }

  .phone-screen img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top center;
    display: block;
  }

  /* Floating callout bubbles */
  .callout {
    position: absolute;
    background: white;
    border-radius: 12px;
    padding: 10px 14px;
    box-shadow: 0 8px 24px rgba(0,0,0,0.12);
    font-size: 12px;
    font-family: var(--font-sans), sans-serif;
    min-width: 140px;
    animation: floatUp 0.6s ease both;
  }

  .callout-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    display: inline-block;
    margin-right: 6px;
    vertical-align: middle;
  }

  .callout-label {
    font-size: 10px;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-bottom: 3px;
  }

  .callout-value {
    font-size: 13px;
    font-weight: 600;
    color: #1a1a18;
  }

  .callout-left {
    right: calc(100% + 18px);
    top: 22%;
    animation-delay: 0.3s;
  }

  .callout-right {
    left: calc(100% + 18px);
    top: 52%;
    animation-delay: 0.5s;
  }

  .callout-bottom {
    left: calc(100% + 18px);
    top: 30%;
    animation-delay: 0.7s;
  }

  @keyframes floatUp {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .platform-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    font-weight: 500;
    background: rgba(59,109,17,0.1);
    color: #3B6D11;
    border-radius: 20px;
    padding: 4px 10px;
    margin-top: 12px;
  }

  @media (max-width: 700px) {
    .st-grid { grid-template-columns: 1fr; gap: 40px; }
    .callout-left, .callout-right, .callout-bottom { display: none; }
    .st-section { padding: 60px 24px; }
  }
      `
      }</style>

      {/* NAV */}
      <LandingNav lang={illLang} />

      {/* HERO */}
      <HeroSequence lang={illLang} />


      {/* ===================== SVEND AI · CONVERGE SCROLL ===================== */}
      <section
        id="svend"
        className="section-pad"
        style={{ background: "#FFFFFF", padding: "110px clamp(20px,6vw,64px) 40px", fontFamily: "var(--font-sans), sans-serif" }}
      >
        <style>{`
          @keyframes svCardFloat { from { opacity: 0; } to { opacity: 1; } }
          @media (max-width: 900px) {
            .sv-converge-stage { display: none !important; }
          }
          @media (max-width: 700px) {
            .sv-capabilities-grid { grid-template-columns: 1fr !important; }
          }
          @media (max-width: 1000px) and (min-width: 701px) {
            .sv-capabilities-grid { grid-template-columns: repeat(2,1fr) !important; }
          }
        `}</style>
        <div style={{ maxWidth: 1180, margin: "0 auto" }}>
          <div style={{ textAlign: "center", maxWidth: 720, margin: "0 auto 24px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 14, marginBottom: 20 }}>
              <span style={{ display: "block", width: 56, height: 1, background: "#C7D2CC" }} />
              <span style={{ fontFamily: "var(--font-mono), monospace", fontSize: 11, fontWeight: 500, letterSpacing: "0.18em", textTransform: "uppercase", color: "#C2410C" }}>
                {illLang === 'da' ? 'Mød Svend' : 'Meet Svend'}
              </span>
              <span style={{ display: "block", width: 56, height: 1, background: "#C7D2CC" }} />
            </div>
            <h2 style={{ fontFamily: "var(--font-serif), serif", fontSize: "clamp(34px,4.6vw,58px)", lineHeight: 1.05, letterSpacing: "-0.025em", fontWeight: 600, margin: "0 0 18px", color: "#10211B" }}>
              {illLang === 'da' ? <>Hele din forretning, <em style={{ fontStyle: "italic", color: "#2F6B48" }}>samlet i én assistent</em></> : <>Your whole business, <em style={{ fontStyle: "italic", color: "#2F6B48" }}>in one assistant</em></>}
            </h2>
            <p style={{ fontSize: 19, lineHeight: 1.6, color: "#597066", margin: "0 auto", maxWidth: 600 }}>
              {illLang === 'da'
                ? 'Fakturaer, kalender, timer, materialer, tilbud og kunder. Scroll ned, og se det hele lande hos Svend — din AI-assistent i lommen.'
                : 'Invoices, calendar, hours, materials, quotes and customers. Scroll down and watch it all land with Svend — your AI assistant in your pocket.'}
            </p>
          </div>

          {/* Stage */}
          <div ref={svendStageRef} className="sv-converge-stage" style={{ position: "relative", height: 797, maxWidth: 1100, margin: "0 auto" }}>
            {/* Phone */}
            <div style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)", width: 400, pointerEvents: "none" }}>
              <Image
                src="/AI_CHAT_HERO.png"
                alt={illLang === 'da' ? 'Svend AI-assistenten på mobilen' : 'The Svend AI assistant on mobile'}
                width={1080}
                height={1610}
                sizes="400px"
                priority
                fetchPriority="high"
                style={{ width: "100%", height: "auto", display: "block" }}
              />
            </div>

            {/* Faktura */}
            <div ref={svCardRef(0)} data-dx="-460" data-dy="-260" data-rot="-7" data-delay="0" style={{ position: "absolute", left: "calc(50% - 250px)", top: "calc(50% - 190px)", width: 210, background: "#FFFFFF", border: "1px solid #DCE3DD", borderRadius: 16, padding: "15px 16px", boxShadow: "0 14px 34px -18px rgba(16,33,27,0.35)", willChange: "transform, opacity" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 9 }}>
                <span style={{ fontFamily: "var(--font-mono), monospace", fontSize: 9.5, letterSpacing: "0.12em", textTransform: "uppercase", color: "#597066" }}>{illLang === 'da' ? 'Faktura · 1042' : 'Invoice · 1042'}</span>
                <span style={{ fontSize: 10, fontWeight: 600, color: "#2F6B48", background: "#E0ECE7", padding: "3px 8px", borderRadius: 999 }}>{illLang === 'da' ? 'Betalt' : 'Paid'}</span>
              </div>
              <div style={{ fontFamily: "var(--font-serif), serif", fontSize: 27, fontWeight: 600, color: "#10211B", lineHeight: 1 }}>kr 12.480</div>
              <div style={{ fontSize: 11.5, color: "#597066", marginTop: 7 }}>{illLang === 'da' ? 'Nyt badeværelse · Anna Jensen' : 'New bathroom · Anna Jensen'}</div>
            </div>

            {/* Kalender */}
            <div ref={svCardRef(1)} data-dx="470" data-dy="-300" data-rot="6" data-delay="0.06" style={{ position: "absolute", left: "calc(50% + 40px)", top: "calc(50% - 230px)", width: 216, background: "#FFFFFF", border: "1px solid #DCE3DD", borderRadius: 16, padding: "15px 16px", boxShadow: "0 14px 34px -18px rgba(16,33,27,0.35)", willChange: "transform, opacity" }}>
              <div style={{ fontFamily: "var(--font-mono), monospace", fontSize: 9.5, letterSpacing: "0.12em", textTransform: "uppercase", color: "#597066", marginBottom: 9 }}>{illLang === 'da' ? 'Kalender · Tirsdag' : 'Calendar · Tuesday'}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "7px 9px", background: "#F3F5F1", borderRadius: 9, marginBottom: 6 }}>
                <span style={{ fontFamily: "var(--font-mono), monospace", fontSize: 10.5, color: "#2F6B48", fontWeight: 500 }}>08–12</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: "#10211B" }}>{illLang === 'da' ? 'Renovering, Bredgade 8' : 'Renovation, Bredgade 8'}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "7px 9px", background: "#F3F5F1", borderRadius: 9 }}>
                <span style={{ fontFamily: "var(--font-mono), monospace", fontSize: 10.5, color: "#2F6B48", fontWeight: 500 }}>13–16</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: "#10211B" }}>{illLang === 'da' ? 'Service, Møllevej 3' : 'Service, Møllevej 3'}</span>
              </div>
            </div>

            {/* Omsætning chart */}
            <div ref={svCardRef(2)} data-dx="-540" data-dy="60" data-rot="5" data-delay="0.12" style={{ position: "absolute", left: "calc(50% - 290px)", top: "calc(50% - 30px)", width: 212, background: "#FFFFFF", border: "1px solid #DCE3DD", borderRadius: 16, padding: "15px 16px", boxShadow: "0 14px 34px -18px rgba(16,33,27,0.35)", willChange: "transform, opacity" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontFamily: "var(--font-mono), monospace", fontSize: 9.5, letterSpacing: "0.12em", textTransform: "uppercase", color: "#597066" }}>{illLang === 'da' ? 'Omsætning' : 'Revenue'}</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 10.5, fontWeight: 600, color: "#2F6B48" }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M3 17l6-6 4 4 8-8" /><path d="M14 7h7v7" /></svg>
                  +18%
                </span>
              </div>
              <div style={{ fontFamily: "var(--font-serif), serif", fontSize: 25, fontWeight: 600, color: "#10211B", lineHeight: 1, marginBottom: 10 }}>kr 86.400</div>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 5, height: 42 }}>
                <span style={{ flex: 1, height: "40%", background: "#DCE3DD", borderRadius: "3px 3px 0 0" }} />
                <span style={{ flex: 1, height: "55%", background: "#DCE3DD", borderRadius: "3px 3px 0 0" }} />
                <span style={{ flex: 1, height: "45%", background: "#DCE3DD", borderRadius: "3px 3px 0 0" }} />
                <span style={{ flex: 1, height: "70%", background: "#9CC7A8", borderRadius: "3px 3px 0 0" }} />
                <span style={{ flex: 1, height: "60%", background: "#9CC7A8", borderRadius: "3px 3px 0 0" }} />
                <span style={{ flex: 1, height: "100%", background: "#F97316", borderRadius: "3px 3px 0 0" }} />
              </div>
            </div>

            {/* Timer */}
            <div ref={svCardRef(3)} data-dx="560" data-dy="20" data-rot="-5" data-delay="0.18" style={{ position: "absolute", left: "calc(50% + 80px)", top: "calc(50% - 40px)", width: 196, background: "#FFFFFF", border: "1px solid #DCE3DD", borderRadius: 16, padding: "15px 16px", boxShadow: "0 14px 34px -18px rgba(16,33,27,0.35)", willChange: "transform, opacity" }}>
              <div style={{ fontFamily: "var(--font-mono), monospace", fontSize: 9.5, letterSpacing: "0.12em", textTransform: "uppercase", color: "#597066", marginBottom: 8 }}>{illLang === 'da' ? 'Timer · Denne uge' : 'Hours · This week'}</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                <span style={{ fontFamily: "var(--font-serif), serif", fontSize: 27, fontWeight: 600, color: "#10211B", lineHeight: 1 }}>37,5</span>
                <span style={{ fontFamily: "var(--font-mono), monospace", fontSize: 12, color: "#597066" }}>{illLang === 'da' ? 'timer' : 'hours'}</span>
              </div>
              <div style={{ height: 6, background: "#EDF1EC", borderRadius: 999, marginTop: 11, overflow: "hidden" }}>
                <span style={{ display: "block", width: "82%", height: "100%", background: "#2F6B48", borderRadius: 999 }} />
              </div>
              <div style={{ fontSize: 11, color: "#597066", marginTop: 7 }}>{illLang === 'da' ? 'Registreret automatisk' : 'Logged automatically'}</div>
            </div>

            {/* Tilbud */}
            <div ref={svCardRef(4)} data-dx="-480" data-dy="340" data-rot="6" data-delay="0.24" style={{ position: "absolute", left: "calc(50% - 260px)", top: "calc(50% + 130px)", width: 204, background: "#FFFFFF", border: "1px solid #DCE3DD", borderRadius: 16, padding: "15px 16px", boxShadow: "0 14px 34px -18px rgba(16,33,27,0.35)", willChange: "transform, opacity" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 9 }}>
                <span style={{ fontFamily: "var(--font-mono), monospace", fontSize: 9.5, letterSpacing: "0.12em", textTransform: "uppercase", color: "#597066" }}>{illLang === 'da' ? 'Tilbud' : 'Quote'}</span>
                <span style={{ fontSize: 10, fontWeight: 600, color: "#A57423", background: "#F5EBD8", padding: "3px 8px", borderRadius: 999 }}>{illLang === 'da' ? 'Sendt' : 'Sent'}</span>
              </div>
              <div style={{ fontFamily: "var(--font-serif), serif", fontSize: 25, fontWeight: 600, color: "#C2410C", lineHeight: 1 }}>kr 4.850</div>
              <div style={{ fontSize: 11.5, color: "#597066", marginTop: 7 }}>{illLang === 'da' ? '6 stikkontakter · inkl. test' : '6 sockets · incl. testing'}</div>
            </div>

            {/* Materialer */}
            <div ref={svCardRef(5)} data-dx="510" data-dy="320" data-rot="-6" data-delay="0.3" style={{ position: "absolute", left: "calc(50% + 60px)", top: "calc(50% + 120px)", width: 212, background: "#FFFFFF", border: "1px solid #DCE3DD", borderRadius: 16, padding: "15px 16px", boxShadow: "0 14px 34px -18px rgba(16,33,27,0.35)", willChange: "transform, opacity" }}>
              <div style={{ fontFamily: "var(--font-mono), monospace", fontSize: 9.5, letterSpacing: "0.12em", textTransform: "uppercase", color: "#597066", marginBottom: 9 }}>{illLang === 'da' ? 'Materialer' : 'Materials'}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 500, color: "#10211B", padding: "4px 0" }}>
                <span style={{ width: 15, height: 15, borderRadius: 5, background: "#E0ECE7", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#2F6B48" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7" /></svg>
                </span>
                <span style={{ flex: 1 }}>Gipsplade 13mm</span>
                <span style={{ fontFamily: "var(--font-mono), monospace", fontSize: 11, color: "#597066" }}>24</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 500, color: "#10211B", padding: "4px 0" }}>
                <span style={{ width: 15, height: 15, borderRadius: 5, background: "#E0ECE7", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#2F6B48" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7" /></svg>
                </span>
                <span style={{ flex: 1 }}>Skruer 4,0×40mm</span>
                <span style={{ fontFamily: "var(--font-mono), monospace", fontSize: 11, color: "#597066" }}>200</span>
              </div>
            </div>

            {/* Kunde */}
            <div ref={svCardRef(6)} data-dx="0" data-dy="480" data-rot="4" data-delay="0.36" style={{ position: "absolute", left: "calc(50% - 104px)", top: "calc(50% + 250px)", width: 208, background: "#FFFFFF", border: "1px solid #DCE3DD", borderRadius: 16, padding: "14px 16px", boxShadow: "0 14px 34px -18px rgba(16,33,27,0.35)", display: "flex", alignItems: "center", gap: 11, willChange: "transform, opacity" }}>
              <span style={{ width: 38, height: 38, borderRadius: "50%", background: "#F4C8A6", color: "#7C3D12", display: "inline-flex", alignItems: "center", justifyContent: "center", fontWeight: 600, fontSize: 14, flexShrink: 0 }}>AJ</span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: "#10211B" }}>Anna Jensen</div>
                <div style={{ fontFamily: "var(--font-mono), monospace", fontSize: 10.5, color: "#597066", marginTop: 2 }}>{illLang === 'da' ? '12 jobs · kr 148.200' : '12 jobs · kr 148,200'}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities — full-bleed gray band */}
      <section
        style={{
          background: "#F3F5F1",
          width: "100vw",
          marginLeft: "calc(50% - 50vw)",
          marginRight: "calc(50% - 50vw)",
          padding: "72px clamp(20px,6vw,64px) 88px",
          fontFamily: "var(--font-sans), sans-serif",
        }}
      >
        <div style={{ maxWidth: 1180, margin: "0 auto" }}>
          <h3 style={{ fontFamily: "var(--font-serif), serif", fontSize: "clamp(34px,4.4vw,52px)", fontWeight: 700, fontStyle: "normal", color: "#10211B", textAlign: "left", margin: "0 0 48px" }}>
            {illLang === 'da' ? 'Svend tager sig af det hele.' : 'Svend takes care of the rest.'}
          </h3>
          <div className="sv-capabilities-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "40px 44px", textAlign: "left" }}>
            {(illLang === 'da' ? [
              { icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6M8 13h8M8 17h6" /></svg>, color: "#2F6B48", title: "Opretter fakturaer", desc: "Sig til, og fakturaen ligger klar til afsendelse — med timer og materialer regnet med." },
              { icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>, color: "#2F6B48", title: "Booker aftaler", desc: "Svend finder en ledig tid og lægger aftalen direkte i din kalender." },
              { icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>, color: "#2F6B48", title: "Registrerer timer", desc: "Arbejdstimer, ferie og fravær logges direkte fra samtalen." },
              { icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 8l-9-5-9 5 9 5 9-5z" /><path d="M3 8v8l9 5 9-5V8" /><path d="M12 13v8" /></svg>, color: "#2F6B48", title: "Holder styr på materialer", desc: "Scan kvitteringen — materialerne føjes automatisk til jobbet." },
              { icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>, color: "#2F6B48", title: "Skriver tilbud", desc: "Pris, ledig tid og fuld opgavebeskrivelse — klar til at sende." },
              { icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" /></svg>, color: "#C2410C", title: "Svarer på alt", desc: "Spørg om kunder, jobs og økonomi — og få svar med det samme." },
            ] : [
              { icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6M8 13h8M8 17h6" /></svg>, color: "#2F6B48", title: "Creates invoices", desc: "Just say the word — the invoice is ready to send, with hours and materials included." },
              { icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>, color: "#2F6B48", title: "Books appointments", desc: "Svend finds a free slot and puts the appointment straight in your calendar." },
              { icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>, color: "#2F6B48", title: "Logs hours", desc: "Work hours, vacation and absence are logged straight from the conversation." },
              { icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 8l-9-5-9 5 9 5 9-5z" /><path d="M3 8v8l9 5 9-5V8" /><path d="M12 13v8" /></svg>, color: "#2F6B48", title: "Tracks materials", desc: "Scan the receipt — the materials are added to the job automatically." },
              { icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>, color: "#2F6B48", title: "Writes quotes", desc: "Price, a free slot and a full job description — ready to send." },
              { icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" /></svg>, color: "#C2410C", title: "Answers anything", desc: "Ask about customers, jobs and finances — and get an answer instantly." },
            ]).map((item, i) => (
              <div key={i}>
                <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", color: item.color, marginBottom: 14 }}>{item.icon}</span>
                <div style={{ fontSize: 16, fontWeight: 600, color: "#10211B", marginBottom: 6 }}>{item.title}</div>
                <p style={{ fontSize: 13.5, lineHeight: 1.6, color: "#597066", margin: 0 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DESKTOP SHOWCASE -- Also on the big screen */}
      <section
        style={{
          background: "#28382F",
          width: "100vw",
          marginLeft: "calc(50% - 50vw)",
          marginRight: "calc(50% - 50vw)",
          padding: "100px clamp(20px,5vw,48px)",
          fontFamily: "var(--font-sans), sans-serif",
        }}
      >
        <div style={{ maxWidth: 1080, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 24, flexWrap: "wrap", marginBottom: 28 }}>
            <div style={{ maxWidth: 560 }}>
              <h2 style={{ fontSize: "clamp(30px,3.4vw,50px)", fontWeight: 800, letterSpacing: "-0.02em", color: "#fff", fontFamily: "var(--font-display), var(--font-sans), sans-serif", lineHeight: 1.08, margin: 0 }}>
                {illLang === 'da' ? 'Også på den store skærm' : 'Also on the big screen'}
              </h2>
              <p style={{ margin: "16px 0 0", fontSize: "clamp(15px,1.4vw,18px)", lineHeight: 1.55, color: "#b8c8bc", fontWeight: 500, fontFamily: "var(--font-display), var(--font-sans), sans-serif" }}>
                {illLang === 'da'
                  ? 'Alt fra appen kører også i browseren på din computer. Planlæg ugen i kalenderen, send fakturaer og følg økonomien hjemme fra kontoret.'
                  : 'Everything in the app also runs in your computer’s browser. Plan the week in the calendar, send invoices, and track finances from the office.'}
              </p>
            </div>
            <div style={{ display: "flex", gap: 6, background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 999, padding: 5 }}>
              {desktopTabs.map((label, i) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => setDesktopView(i)}
                  style={{
                    appearance: "none",
                    cursor: "pointer",
                    border: "none",
                    padding: "9px 18px",
                    borderRadius: 999,
                    fontFamily: "var(--font-display), var(--font-sans), sans-serif",
                    fontSize: 14.5,
                    fontWeight: 600,
                    color: i === desktopView ? "#1e2a22" : "#b8c8bc",
                    background: i === desktopView ? "#fff" : "none",
                    transition: "color .15s ease, background .15s ease, transform .15s ease",
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div style={{ background: "#fff", borderRadius: 16, overflow: "hidden", boxShadow: "0 40px 90px -30px rgba(0,0,0,0.55)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 16px", background: "#f6f8f4", borderBottom: "1px solid #e7ece3" }}>
              <span style={{ width: 9, height: 9, borderRadius: 999, background: "#d7dfd6" }} />
              <span style={{ width: 9, height: 9, borderRadius: 999, background: "#d7dfd6" }} />
              <span style={{ width: 9, height: 9, borderRadius: 999, background: "#d7dfd6" }} />
              <span style={{ marginLeft: 10, fontFamily: "var(--font-mono), monospace", fontSize: 11.5, color: "#54635a" }}>app.svendetid.dk</span>
            </div>
            <div style={{ position: "relative" }}>
              {desktopShots.map((src, i) => (
                <img
                  key={src}
                  src={src}
                  alt={
                    illLang === 'da'
                      ? `Svendetid i browseren: ${desktopTabs[i].toLowerCase()}`
                      : `Svendetid in the browser: ${desktopTabs[i].toLowerCase()}`
                  }
                  style={
                    i === desktopView
                      ? { width: "100%", display: "block" }
                      : { width: "100%", display: "block", position: "absolute", inset: 0, opacity: 0, pointerEvents: "none" }
                  }
                />
              ))}
            </div>
          </div>

          <p style={{ marginTop: 18, fontSize: 13.5, color: "#b8c8bc", fontWeight: 500, textAlign: "center" }}>
            {illLang === 'da'
              ? 'Du logger ind med samme konto, og alt er synkroniseret med appen.'
              : 'You sign in with the same account, and everything stays in sync with the app.'}
          </p>
        </div>
      </section>

      {/* SCAN FAKTURA -- Invoice Scan Illustration */}
      <section style={{ background: "#ffffff", padding: "80px clamp(20px, 5vw, 48px)" }}>
        <style>{`
          .ill2 { --loop: 13s; }
          .ill2-head { text-align: center; margin-bottom: 56px; }
          .ill2-head h2 { font-size: clamp(28px, 3.2vw, 48px); font-weight: 800; letter-spacing: -.02em; color: #1e2a22; font-family: var(--font-display), var(--font-sans), sans-serif; line-height: 1.08; }
          .ill2-head p { margin: 16px auto 0; max-width: 560px; font-size: clamp(15px, 1.4vw, 18px); line-height: 1.55; color: #54635a; font-weight: 500; font-family: var(--font-display), var(--font-sans), sans-serif; }
          .ill2-row { display: flex; align-items: stretch; gap: 0; }
          .ill2-card { position: relative; flex: 1 1 0; background: #fff; border: 1px solid #e7ece3; border-radius: 20px; padding: clamp(18px,2vw,26px); box-shadow: 0 8px 24px -12px rgba(38,60,46,.3); display: flex; flex-direction: column; }
          .ill2-card.ill-s1 { animation: ill-act1 var(--loop) ease-in-out infinite; }
          .ill2-card.ill-s2 { animation: ill-act2 var(--loop) ease-in-out infinite; }
          .ill2-card.ill-s3 { animation: ill-act3 var(--loop) ease-in-out infinite; }
          @keyframes ill-act1 { 0%,2%{box-shadow:0 8px 24px -12px rgba(38,60,46,.3);transform:translateY(0)} 6%,26%{box-shadow:0 16px 40px -14px rgba(38,60,46,.4);transform:translateY(-6px)} 30%,86%{box-shadow:0 8px 24px -12px rgba(38,60,46,.3);transform:translateY(0)} 90%,96%{box-shadow:0 16px 40px -14px rgba(38,60,46,.4);transform:translateY(-4px)} 100%{box-shadow:0 8px 24px -12px rgba(38,60,46,.3);transform:translateY(0)} }
          @keyframes ill-act2 { 0%,30%{box-shadow:0 8px 24px -12px rgba(38,60,46,.3);transform:translateY(0)} 34%,50%{box-shadow:0 16px 40px -14px rgba(38,60,46,.4);transform:translateY(-6px)} 54%,86%{box-shadow:0 8px 24px -12px rgba(38,60,46,.3);transform:translateY(0)} 90%,96%{box-shadow:0 16px 40px -14px rgba(38,60,46,.4);transform:translateY(-4px)} 100%{box-shadow:0 8px 24px -12px rgba(38,60,46,.3);transform:translateY(0)} }
          @keyframes ill-act3 { 0%,54%{box-shadow:0 8px 24px -12px rgba(38,60,46,.3);transform:translateY(0)} 58%,76%{box-shadow:0 16px 40px -14px rgba(38,60,46,.4);transform:translateY(-6px)} 80%,86%{box-shadow:0 8px 24px -12px rgba(38,60,46,.3);transform:translateY(0)} 90%,96%{box-shadow:0 16px 40px -14px rgba(38,60,46,.4);transform:translateY(-4px)} 100%{box-shadow:0 8px 24px -12px rgba(38,60,46,.3);transform:translateY(0)} }
          .ill2-num { position: absolute; top: -18px; left: 22px; width: 36px; height: 36px; border-radius: 11px; background: #3a4d42; color: #fff; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 16px; box-shadow: 0 6px 14px -6px rgba(47,63,55,.7); z-index: 4; font-family: var(--font-display), var(--font-sans), sans-serif; }
          .ill2-art { height: clamp(180px,18vw,268px); border-radius: 14px; background: #eff4ed; border: 1px solid #d4e0d2; position: relative; overflow: hidden; margin-top: 6px; flex-shrink: 0; }
          .ill2-card h3 { font-size: clamp(16px,1.6vw,22px); font-weight: 800; letter-spacing: -.01em; margin-top: 18px; font-family: var(--font-display), var(--font-sans), sans-serif; color: #1e2a22; }
          .ill2-card .ill2-cap { font-size: clamp(13px,1.1vw,15px); line-height: 1.5; color: #54635a; font-weight: 500; margin-top: 7px; font-family: var(--font-display), var(--font-sans), sans-serif; }
          .ill2-conn { flex: 0 0 clamp(32px,4vw,72px); align-self: center; position: relative; height: 5px; margin-top: 36px; }
          .ill2-conn .ill2-track { position: absolute; inset: 0; border-radius: 3px; background: repeating-linear-gradient(90deg,#cdddc8 0 7px,transparent 7px 14px); animation: ill-march var(--loop) linear infinite; opacity: .9; }
          @keyframes ill-march { to { background-position: -140px 0; } }
          .ill2-conn .ill2-pulse { position: absolute; top: 50%; left: 0; width: 13px; height: 13px; margin: -6.5px 0 0 -6.5px; border-radius: 50%; background: #2e7d4f; }
          .ill2-conn.ill2-c1 .ill2-pulse { animation: ill-flow1 var(--loop) ease-in-out infinite; }
          .ill2-conn.ill2-c2 .ill2-pulse { animation: ill-flow2 var(--loop) ease-in-out infinite; }
          @keyframes ill-flow1 { 0%,18%{left:0;opacity:0} 22%{opacity:1} 30%{left:100%;opacity:1} 34%,100%{left:100%;opacity:0} }
          @keyframes ill-flow2 { 0%,42%{left:0;opacity:0} 46%{opacity:1} 54%{left:100%;opacity:1} 58%,100%{left:100%;opacity:0} }
          .ill2-receipt { position: absolute; left: 50%; top: 20px; transform: translateX(-50%) rotate(-3deg); width: clamp(120px,13vw,188px); background: #fff; border-radius: 7px; padding: 12px 14px 18px; box-shadow: 0 12px 24px -12px rgba(40,55,45,.5); }
          .ill2-r-store { font-size: clamp(7px,0.7vw,9.5px); font-weight: 800; letter-spacing: .06em; color: #1e2a22; border-bottom: 1.5px dashed #d9ddd2; padding-bottom: 6px; text-align: center; font-family: var(--font-display), var(--font-sans), sans-serif; }
          .ill2-r-li { display: flex; justify-content: space-between; gap: 6px; font-size: clamp(7px,0.7vw,9.5px); margin-top: 6px; color: #3c4942; font-weight: 600; white-space: nowrap; font-family: var(--font-display), var(--font-sans), sans-serif; }
          .ill2-r-li span:last-child { color: #6a7770; font-weight: 700; }
          .ill2-r-li.ill2-muted { opacity: .4; }
          .ill2-scanframe { position: absolute; left: 50%; top: 18px; transform: translateX(-50%) rotate(-3deg); width: clamp(130px,14vw,206px); height: clamp(140px,15vw,228px); z-index: 3; }
          .ill2-corner { position: absolute; width: 24px; height: 24px; border: 3px solid #2e7d4f; border-radius: 3px; }
          .ill2-corner.tl { top:0;left:0;border-right:0;border-bottom:0; }
          .ill2-corner.tr { top:0;right:0;border-left:0;border-bottom:0; }
          .ill2-corner.bl { bottom:0;left:0;border-right:0;border-top:0; }
          .ill2-corner.br { bottom:0;right:0;border-left:0;border-top:0; }
          .ill2-scanline { position: absolute; left: 5px; right: 5px; height: 2.5px; border-radius: 3px; background: linear-gradient(90deg,transparent,#4a9b6a,transparent); box-shadow: 0 0 12px 3px rgba(74,155,106,.55); animation: ill2-sweep var(--loop) ease-in-out infinite; opacity: 0; }
          @keyframes ill2-sweep { 0%,4%{top:5px;opacity:0} 7%{opacity:1} 24%{top:calc(100% - 5px);opacity:1} 28%{opacity:0} 100%{opacity:0} }
          .ill2-cam-chip { position: absolute; bottom: 12px; left: 50%; transform: translateX(-50%); display: flex; align-items: center; gap: 7px; background: #3a4d42; color: #fff; font-size: clamp(10px,1vw,13px); font-weight: 700; padding: 7px 13px; border-radius: 999px; animation: ill2-chip1 var(--loop) ease-in-out infinite; font-family: var(--font-display), var(--font-sans), sans-serif; white-space: nowrap; }
          .ill2-cam-chip svg { width: 14px; height: 14px; flex-shrink: 0; }
          @keyframes ill2-chip1 { 0%,3%{opacity:0;transform:translateX(-50%) translateY(8px)} 8%,26%{opacity:1;transform:translateX(-50%) translateY(0)} 32%,100%{opacity:0;transform:translateX(-50%) translateY(8px)} }
          .ill2-scan-load { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px; color: #2e7d4f; font-weight: 700; font-size: clamp(11px,1.1vw,14px); animation: ill2-loadFade var(--loop) ease-in-out infinite; opacity: 0; font-family: var(--font-display), var(--font-sans), sans-serif; }
          @keyframes ill2-loadFade { 0%,30%{opacity:0} 34%{opacity:1} 39%{opacity:1} 42%{opacity:0} 100%{opacity:0} }
          .ill2-spark { width: 22px; height: 22px; animation: ill2-spin 2.4s linear infinite; }
          @keyframes ill2-spin { to { transform: rotate(360deg); } }
          .ill2-rows { position: absolute; left: 12px; right: 12px; top: 14px; display: flex; flex-direction: column; gap: 7px; }
          .ill2-erow { display: flex; align-items: center; gap: 7px; background: #fff; border: 1px solid #d4e0d2; border-radius: 9px; padding: 7px 8px; opacity: 0; transform: translateY(8px); }
          .ill2-erow .ill2-tick { flex: 0 0 16px; width: 16px; height: 16px; border-radius: 50%; background: #2e7d4f; display: flex; align-items: center; justify-content: center; }
          .ill2-erow .ill2-tick svg { width: 9px; height: 9px; }
          .ill2-erow .ill2-nm { flex: 1; font-size: clamp(10px,0.9vw,12.5px); font-weight: 700; color: #1e2a22; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-family: var(--font-display), var(--font-sans), sans-serif; }
          .ill2-erow .ill2-qty { flex: 0 0 auto; font-size: clamp(9px,0.8vw,11px); font-weight: 800; color: #2e7d4f; background: #e6f1e6; border-radius: 6px; padding: 3px 7px; white-space: nowrap; font-family: var(--font-display), var(--font-sans), sans-serif; }
          .ill2-erow.r1 { animation: ill-rev1 var(--loop) ease-in-out infinite; }
          .ill2-erow.r2 { animation: ill-rev2 var(--loop) ease-in-out infinite; }
          .ill2-erow.r3 { animation: ill-rev3 var(--loop) ease-in-out infinite; }
          .ill2-erow.r4 { animation: ill-rev4 var(--loop) ease-in-out infinite; }
          @keyframes ill-rev1 { 0%,42%{opacity:0;transform:translateY(8px)} 46%,86%{opacity:1;transform:translateY(0)} 92%,100%{opacity:0;transform:translateY(-6px)} }
          @keyframes ill-rev2 { 0%,45%{opacity:0;transform:translateY(8px)} 49%,86%{opacity:1;transform:translateY(0)} 92%,100%{opacity:0;transform:translateY(-6px)} }
          @keyframes ill-rev3 { 0%,48%{opacity:0;transform:translateY(8px)} 52%,86%{opacity:1;transform:translateY(0)} 92%,100%{opacity:0;transform:translateY(-6px)} }
          @keyframes ill-rev4 { 0%,51%{opacity:0;transform:translateY(8px)} 55%,86%{opacity:1;transform:translateY(0)} 92%,100%{opacity:0;transform:translateY(-6px)} }
          .ill2-auto-badge { position: absolute; bottom: 12px; left: 50%; transform: translateX(-50%); display: inline-flex; align-items: center; gap: 6px; font-size: clamp(10px,0.9vw,12px); font-weight: 800; color: #2e7d4f; background: #e4f0e3; border: 1px solid #cfe3cd; padding: 6px 11px; border-radius: 999px; animation: ill2-autob var(--loop) ease-in-out infinite; opacity: 0; font-family: var(--font-display), var(--font-sans), sans-serif; white-space: nowrap; }
          .ill2-auto-badge svg { width: 12px; height: 12px; }
          @keyframes ill2-autob { 0%,52%{opacity:0;transform:translateX(-50%) scale(.9)} 57%,86%{opacity:1;transform:translateX(-50%) scale(1)} 92%,100%{opacity:0;transform:translateX(-50%) scale(.9)} }
          .ill2-jobcard { position: absolute; left: 14px; right: 14px; top: 14px; background: #fff; border: 1px solid #d4e0d2; border-radius: 12px; overflow: hidden; }
          .ill2-job-top { display: flex; align-items: center; gap: 8px; padding: 9px 11px; border-bottom: 1px solid #eef2ec; }
          .ill2-job-top .ill2-dot { width: 8px; height: 8px; border-radius: 50%; background: #2e7d4f; flex-shrink: 0; }
          .ill2-job-top .ill2-jt { font-size: clamp(10px,1vw,13px); font-weight: 800; color: #1e2a22; white-space: nowrap; font-family: var(--font-display), var(--font-sans), sans-serif; }
          .ill2-job-top .ill2-pill { margin-left: auto; font-size: clamp(9px,0.8vw,10.5px); font-weight: 700; color: #2e7d4f; background: #e6f1e6; padding: 3px 8px; border-radius: 999px; font-family: var(--font-display), var(--font-sans), sans-serif; white-space: nowrap; }
          .ill2-mat-head { display: flex; align-items: center; justify-content: space-between; padding: 9px 11px 3px; font-size: clamp(8px,0.8vw,11px); font-weight: 800; letter-spacing: .08em; color: #54635a; text-transform: uppercase; font-family: var(--font-display), var(--font-sans), sans-serif; }
          .ill2-mat-head .ill2-cnt { color: #2e7d4f; background: #e6f1e6; border-radius: 6px; padding: 2px 7px; letter-spacing: 0; }
          .ill2-mlist { padding: 3px 11px 10px; display: flex; flex-direction: column; gap: 6px; }
          .ill2-mrow { display: flex; align-items: center; gap: 8px; font-size: clamp(10px,0.9vw,12px); font-weight: 600; color: #3c4942; opacity: 0; transform: translateX(-8px); font-family: var(--font-display), var(--font-sans), sans-serif; }
          .ill2-mrow .ill2-mt { width: 14px; height: 14px; border-radius: 4px; background: #e6f1e6; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
          .ill2-mrow .ill2-mt svg { width: 8px; height: 8px; }
          .ill2-mrow .ill2-mq { margin-left: auto; font-weight: 800; color: #6a7770; }
          .ill2-mrow.m1 { animation: ill-slidein var(--loop) ease-in-out infinite; }
          .ill2-mrow.m2 { animation: ill-slidein2 var(--loop) ease-in-out infinite; }
          .ill2-mrow.m3 { animation: ill-slidein3 var(--loop) ease-in-out infinite; }
          @keyframes ill-slidein  { 0%,55%{opacity:0;transform:translateX(-8px)} 60%,88%{opacity:1;transform:translateX(0)} 94%,100%{opacity:0} }
          @keyframes ill-slidein2 { 0%,58%{opacity:0;transform:translateX(-8px)} 63%,88%{opacity:1;transform:translateX(0)} 94%,100%{opacity:0} }
          @keyframes ill-slidein3 { 0%,61%{opacity:0;transform:translateX(-8px)} 66%,88%{opacity:1;transform:translateX(0)} 94%,100%{opacity:0} }
          .ill2-confirm { position: absolute; bottom: 12px; left: 14px; right: 14px; display: flex; align-items: center; justify-content: center; gap: 8px; background: #3a4d42; color: #fff; font-size: clamp(11px,1vw,14px); font-weight: 800; padding: 9px; border-radius: 10px; opacity: 0; animation: ill2-conf var(--loop) ease-in-out infinite; white-space: nowrap; font-family: var(--font-display), var(--font-sans), sans-serif; }
          .ill2-confirm svg { width: 15px; height: 15px; flex: 0 0 auto; }
          @keyframes ill2-conf { 0%,64%{opacity:0;transform:translateY(8px)} 70%,88%{opacity:1;transform:translateY(0)} 94%,100%{opacity:0;transform:translateY(8px)} }
          .ill2-foot { display: flex; justify-content: center; margin-top: 48px; }
          .ill2-timesave { display: inline-flex; align-items: center; gap: clamp(8px,1vw,13px); background: #3a4d42; color: #fff; padding: clamp(10px,1.2vw,15px) clamp(16px,2vw,28px); border-radius: 999px; box-shadow: 0 12px 36px -16px rgba(38,60,46,.45); font-weight: 700; white-space: nowrap; font-family: var(--font-display), var(--font-sans), sans-serif; }
          .ill2-clk { width: 20px; height: 20px; flex: 0 0 auto; stroke: #9cc7a8; }
          .ill2-big { font-size: clamp(14px,1.3vw,18px); font-weight: 800; }
          .ill2-sep { width: 1px; height: 20px; background: rgba(255,255,255,.25); display: inline-block; }
          .ill2-sub { font-size: clamp(12px,1.1vw,14.5px); font-weight: 600; color: #d6e6d8; }
          @media (max-width: 700px) {
            .ill2-row { flex-direction: column; gap: 32px; }
            .ill2-conn { display: none; }
          }
          @media (prefers-reduced-motion: reduce) {
            .ill2-card,.ill2-scanline,.ill2-cam-chip,.ill2-scan-load,.ill2-erow,.ill2-auto-badge,.ill2-mrow,.ill2-confirm,.ill2-spark { animation: none !important; }
            .ill2-erow,.ill2-mrow,.ill2-confirm { opacity: 1 !important; transform: none !important; }
          }

          /* ===================== AUTO-OFFER · VARIATION B ===================== */
          @keyframes ill-spin{to{transform:rotate(360deg)}}
          .offb-stage{--bloop:15s}
          .offb-form{animation:offbForm var(--bloop) ease-in-out infinite}
          .offb-calc{opacity:0;animation:offbCalc var(--bloop) ease-in-out infinite}
          .offb-offer{opacity:0;animation:offbOffer var(--bloop) ease-in-out infinite}
          @keyframes offbForm{0%{opacity:0}3%,30%{opacity:1}37%,100%{opacity:0}}
          @keyframes offbCalc{0%,31%{opacity:0}37%,43%{opacity:1}49%,100%{opacity:0}}
          @keyframes offbOffer{0%,45%{opacity:0}51%,93%{opacity:1}99%,100%{opacity:0}}
          .offb-f1{opacity:0;animation:offbF1 var(--bloop) ease-in-out infinite}
          .offb-f2{opacity:0;animation:offbF2 var(--bloop) ease-in-out infinite}
          .offb-f3{opacity:0;animation:offbF3 var(--bloop) ease-in-out infinite}
          @keyframes offbF1{0%,4%{opacity:0;transform:translateY(6px)}9%,30%{opacity:1;transform:translateY(0)}36%,100%{opacity:0}}
          @keyframes offbF2{0%,10%{opacity:0;transform:translateY(6px)}15%,30%{opacity:1;transform:translateY(0)}36%,100%{opacity:0}}
          @keyframes offbF3{0%,16%{opacity:0;transform:translateY(6px)}21%,30%{opacity:1;transform:translateY(0)}36%,100%{opacity:0}}
          .offb-co1{opacity:0;animation:offbCo1 var(--bloop) ease-in-out infinite}
          .offb-co2{opacity:0;animation:offbCo2 var(--bloop) ease-in-out infinite}
          .offb-co3{opacity:0;animation:offbCo3 var(--bloop) ease-in-out infinite}
          @keyframes offbCo1{0%,53%{opacity:0;transform:translateY(10px) scale(.96)}58%,92%{opacity:1;transform:translateY(0) scale(1)}98%,100%{opacity:0}}
          @keyframes offbCo2{0%,59%{opacity:0;transform:translateY(10px) scale(.96)}64%,92%{opacity:1;transform:translateY(0) scale(1)}98%,100%{opacity:0}}
          @keyframes offbCo3{0%,65%{opacity:0;transform:translateY(10px) scale(.96)}70%,92%{opacity:1;transform:translateY(0) scale(1)}98%,100%{opacity:0}}
          .offb-spin{animation:ill-spin 1.5s linear infinite}
          @media (max-width: 920px){
            .offb-co1, .offb-co2, .offb-co3 { display:none !important; }
            .offer-callouts-stack { display:flex !important; }
            .how-steps-grid { grid-template-columns:1fr !important; }
          }
          @media (max-width: 760px){
            .how-steps-grid { grid-template-columns:1fr !important; }
          }
          @media (prefers-reduced-motion: reduce){
            .offb-form,.offb-calc,.offb-offer,.offb-f1,.offb-f2,.offb-f3,
            .offb-co1,.offb-co2,.offb-co3,.offb-spin{animation:none !important}
            .offb-form{opacity:0 !important}
            .offb-calc{opacity:0 !important}
            .offb-offer,.offb-f1,.offb-f2,.offb-f3,
            .offb-co1,.offb-co2,.offb-co3{opacity:1 !important;transform:none !important}
          }
        `}</style>

        <div className="ill2" style={{ maxWidth: 1180, margin: "0 auto" }}>
          <div className="ill2-head">
            <h2>{illLang === 'da' ? 'Fra kvittering til job på sekunder' : 'From receipt to job in seconds'}</h2>
            <p>{illLang === 'da' ? 'Scan fakturaen fra byggemarkedet. Appen læser materialerne automatisk — du sparer tid og slipper for manuel indtastning.' : 'Scan the invoice from the store. The app reads the materials automatically — you save time and skip manual entry.'}</p>
          </div>

          <div className="ill2-row">
            {/* Card 1 */}
            <div className="ill2-card ill-s1">
              <div className="ill2-num">1</div>
              <div className="ill2-art">
                <div className="ill2-receipt">
                  <div className="ill2-r-store">{illLang === 'da' ? 'BYGGECENTER · KVITTERING' : 'HARDWARE STORE · RECEIPT'}</div>
                  <div className="ill2-r-li"><span>Skruer 4,0×40mm</span><span>kr 89</span></div>
                  <div className="ill2-r-li"><span>Gipsplade 13mm</span><span>kr 245</span></div>
                  <div className="ill2-r-li"><span>Maling hvid 10L</span><span>kr 399</span></div>
                  <div className="ill2-r-li ill2-muted"><span>Fugemasse</span><span>kr 64</span></div>
                  <div className="ill2-r-li ill2-muted"><span>Træskruer 5×60</span><span>kr 112</span></div>
                </div>
                <div className="ill2-scanframe">
                  <div className="ill2-corner tl"/><div className="ill2-corner tr"/>
                  <div className="ill2-corner bl"/><div className="ill2-corner br"/>
                  <div className="ill2-scanline"/>
                </div>
                <div className="ill2-cam-chip">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3Z"/>
                    <circle cx="12" cy="13" r="3.5"/>
                  </svg>
                  {illLang === 'da' ? 'Tag foto' : 'Take photo'}
                </div>
              </div>
              <h3>{illLang === 'da' ? 'Scan fakturaen' : 'Scan the invoice'}</h3>
              <p className="ill2-cap">{illLang === 'da' ? 'Tag ét foto af fakturaen eller kvitteringen fra byggemarkedet.' : 'Take one photo of the invoice or receipt from the store.'}</p>
            </div>

            <div className="ill2-conn ill2-c1"><div className="ill2-track"/><div className="ill2-pulse"/></div>

            {/* Card 2 */}
            <div className="ill2-card ill-s2">
              <div className="ill2-num">2</div>
              <div className="ill2-art">
                <div className="ill2-scan-load">
                  <svg className="ill2-spark" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1"/>
                  </svg>
                  {illLang === 'da' ? 'Scanner faktura…' : 'Scanning invoice…'}
                </div>
                <div className="ill2-rows">
                  <div className="ill2-erow r1"><span className="ill2-tick"><svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7"/></svg></span><span className="ill2-nm">Skruer 4,0×40mm</span><span className="ill2-qty">200 stk</span></div>
                  <div className="ill2-erow r2"><span className="ill2-tick"><svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7"/></svg></span><span className="ill2-nm">Gipsplade 13mm</span><span className="ill2-qty">24 stk</span></div>
                  <div className="ill2-erow r3"><span className="ill2-tick"><svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7"/></svg></span><span className="ill2-nm">Maling hvid 10L</span><span className="ill2-qty">4 stk</span></div>
                  <div className="ill2-erow r4"><span className="ill2-tick"><svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7"/></svg></span><span className="ill2-nm">Træskruer 5×60mm</span><span className="ill2-qty">120 stk</span></div>
                </div>
                <div className="ill2-auto-badge">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1"/></svg>
                  {illLang === 'da' ? 'Aflæst automatisk' : 'Read automatically'}
                </div>
              </div>
              <h3>{illLang === 'da' ? 'Appen læser ordren' : 'The app reads the order'}</h3>
              <p className="ill2-cap">{illLang === 'da' ? 'Materialer og antal trækkes ud automatisk — ingen manuel indtastning.' : 'Materials and quantities are extracted automatically — no manual entry.'}</p>
            </div>

            <div className="ill2-conn ill2-c2"><div className="ill2-track"/><div className="ill2-pulse"/></div>

            {/* Card 3 */}
            <div className="ill2-card ill-s3">
              <div className="ill2-num">3</div>
              <div className="ill2-art">
                <div className="ill2-jobcard">
                  <div className="ill2-job-top">
                    <span className="ill2-dot"/>
                    <span className="ill2-jt">{illLang === 'da' ? 'Job · Renovering' : 'Job · Renovation'}</span>
                    <span className="ill2-pill">{illLang === 'da' ? 'I gang' : 'In progress'}</span>
                  </div>
                  <div className="ill2-mat-head">
                    <span>{illLang === 'da' ? 'Materialer' : 'Materials'}</span>
                    <span className="ill2-cnt">3</span>
                  </div>
                  <div className="ill2-mlist">
                    <div className="ill2-mrow m1"><span className="ill2-mt"><svg viewBox="0 0 24 24" fill="none" stroke="#2e7d4f" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7"/></svg></span><span>Skruer 4,0×40mm</span><span className="ill2-mq">200</span></div>
                    <div className="ill2-mrow m2"><span className="ill2-mt"><svg viewBox="0 0 24 24" fill="none" stroke="#2e7d4f" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7"/></svg></span><span>Gipsplade 13mm</span><span className="ill2-mq">24</span></div>
                    <div className="ill2-mrow m3"><span className="ill2-mt"><svg viewBox="0 0 24 24" fill="none" stroke="#2e7d4f" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7"/></svg></span><span>Maling hvid 10L</span><span className="ill2-mq">4</span></div>
                  </div>
                </div>
                <div className="ill2-confirm">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M8 12l3 3 5-6"/></svg>
                  {illLang === 'da' ? 'Tilføjet til jobbet' : 'Added to the job'}
                </div>
              </div>
              <h3>{illLang === 'da' ? 'Føj til jobbet' : 'Add to the job'}</h3>
              <p className="ill2-cap">{illLang === 'da' ? 'Bekræft, og materialerne ligger klar på jobbet — på sekunder.' : 'Confirm, and the materials are ready on the job — in seconds.'}</p>
            </div>
          </div>

          <div className="ill2-foot">
            <div className="ill2-timesave">
              <svg className="ill2-clk" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
              <span className="ill2-big">{illLang === 'da' ? 'Helt automatisk' : 'Fully automatic'}</span>
              <span className="ill2-sep"/>
              <span className="ill2-sub">{illLang === 'da' ? 'Sparer tid på hver eneste faktura' : 'Saves time on every single invoice'}</span>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== AUTO-OFFER · Tilbud, der skriver sig selv ===================== */}
      <section
        id="offer"
        className="section-pad"
        style={{ background: "#FFFFFF", padding: "100px clamp(20px,6vw,64px)", position: "relative", overflow: "hidden", fontFamily: "var(--font-sans), sans-serif" }}
      >
        <div style={{ maxWidth: 1180, margin: "0 auto" }}>
          <div style={{ textAlign: "center", maxWidth: 760, margin: "0 auto 36px" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, fontFamily: "var(--font-mono), monospace", fontSize: 11, fontWeight: 500, letterSpacing: "0.16em", textTransform: "uppercase", color: "#C2410C", background: "#FFEFDD", border: "1px solid #FBCFA6", padding: "7px 14px", borderRadius: 999, marginBottom: 22 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2l1.6 4.9L18.5 8l-4.9 1.1L12 14l-1.6-4.9L5.5 8l4.9-1.1L12 2zm6 11l.9 2.6L21.5 16l-2.6.4L18 19l-.9-2.6L14.5 16l2.6-.4L18 13zM6 14l.7 2L9 16.7 7 17.4 6 19.5 5 17.4 3 16.7 5 16 6 14z"/></svg>
              {illLang === 'da' ? 'Automatisk tilbud' : 'Automatic quote'}
            </div>
            <h2 style={{ fontFamily: "var(--font-serif), serif", fontSize: "clamp(34px,4.6vw,56px)", lineHeight: 1.05, letterSpacing: "-0.025em", fontWeight: 600, margin: "0 0 18px", color: "#10211B" }}>
              {illLang === 'da' ? <>Tilbud, der <em style={{ fontStyle: "italic", color: "#2F6B48" }}>skriver sig selv</em></> : <>Quotes that <em style={{ fontStyle: "italic", color: "#2F6B48" }}>write themselves</em></>}
            </h2>
            <p style={{ fontSize: 19, lineHeight: 1.6, color: "#597066", margin: "0 auto", maxWidth: 620 }}>
              {illLang === 'da'
                ? 'Del ét link med din kunde. De beskriver opgaven og hvornår — Svendetid regner prisen ud, finder en ledig tid og skriver en fuld opgavebeskrivelse. Du sparer timer på hvert eneste tilbud.'
                : 'Share one link with your customer. They describe the job and when — Svendetid works out the price, finds a free slot and writes a full job description. You save hours on every single quote.'}
            </p>
          </div>

          <div className="offb-stage" style={{ display: "flex", flexDirection: "column", alignItems: "center", background: "#F3F5F1", border: "1px solid #DCE3DD", borderRadius: 24, padding: "44px 30px 48px", boxShadow: "0 10px 30px -24px rgba(16,33,27,0.18)" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 9, background: "#16312A", borderRadius: 999, padding: "9px 16px 9px 14px", marginBottom: 38, maxWidth: "100%", flexWrap: "wrap", justifyContent: "center" }}>
              <span style={{ width: 26, height: 26, borderRadius: "50%", background: "#F97316", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", flex: "0 0 auto" }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
              </span>
              <span style={{ fontFamily: "var(--font-mono), monospace", fontSize: 13, color: "#EEF4F0" }}>svendetid.dk/tilbud/anna-el</span>
              <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "#9CC7A8", fontFamily: "var(--font-mono), monospace", marginLeft: 6 }}>{illLang === 'da' ? 'Dit link' : 'Your link'}</span>
            </div>

            <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center", gap: 0, width: "100%" }}>
              {/* Floating callouts (desktop) */}
              <div className="offb-co1" style={{ position: "absolute", right: "calc(50% + 178px)", top: 60, width: 208, textAlign: "left", background: "#FFFFFF", border: "1px solid #DCE3DD", borderRadius: 14, padding: "13px 15px", boxShadow: "0 14px 34px -20px rgba(16,33,27,0.4)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 7 }}>
                  <span style={{ width: 24, height: 24, borderRadius: 7, background: "#FFEFDD", display: "flex", alignItems: "center", justifyContent: "center", color: "#C2410C", flex: "0 0 auto" }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="2.5"/></svg>
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: "#597066" }}>{illLang === 'da' ? 'Pris beregnet automatisk' : 'Price calculated automatically'}</span>
                </div>
                <div style={{ fontFamily: "var(--font-serif), serif", fontSize: 26, fontWeight: 600, color: "#C2410C", lineHeight: 1 }}>kr 4.850</div>
              </div>
              <div className="offb-co2" style={{ position: "absolute", left: "calc(50% + 178px)", top: 120, width: 206, textAlign: "left", background: "#FFFFFF", border: "1px solid #DCE3DD", borderRadius: 14, padding: "13px 15px", boxShadow: "0 14px 34px -20px rgba(16,33,27,0.4)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 7 }}>
                  <span style={{ width: 24, height: 24, borderRadius: 7, background: "#E0ECE7", display: "flex", alignItems: "center", justifyContent: "center", color: "#2F6B48", flex: "0 0 auto" }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: "#597066" }}>{illLang === 'da' ? 'Ledig tid fundet' : 'Free slot found'}</span>
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#10211B" }}>Tir 18. jun · 08–12</div>
              </div>
              <div className="offb-co3" style={{ position: "absolute", right: "calc(50% + 184px)", top: 308, width: 202, textAlign: "left", background: "#FFFFFF", border: "1px solid #DCE3DD", borderRadius: 14, padding: "13px 15px", boxShadow: "0 14px 34px -20px rgba(16,33,27,0.4)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 6 }}>
                  <span style={{ width: 24, height: 24, borderRadius: 7, background: "#E4ECFA", display: "flex", alignItems: "center", justifyContent: "center", color: "#2056C0", flex: "0 0 auto" }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M8 13h8M8 17h6"/></svg>
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: "#597066" }}>{illLang === 'da' ? 'Opgave beskrevet' : 'Job described'}</span>
                </div>
                <div style={{ fontSize: 12.5, lineHeight: 1.45, color: "#3c4942" }}>{illLang === 'da' ? 'Demontering + montering af 6 jordede stikkontakter, inkl. test.' : 'Removal + fitting of 6 earthed sockets, incl. testing.'}</div>
              </div>

              {/* iPhone */}
              <div style={{ position: "relative", width: 300, flex: "0 0 auto", borderRadius: 56, padding: 3, background: "linear-gradient(150deg,#86868a,#3a3a3c 22%,#1c1c1e 58%,#4a4a4c 100%)", boxShadow: "0 46px 92px -38px rgba(16,33,27,0.62),0 4px 14px rgba(0,0,0,0.35)" }}>
                <div style={{ position: "absolute", left: -2, top: 120, width: 3, height: 28, borderRadius: "3px 0 0 3px", background: "linear-gradient(#626264,#2c2c2e)" }} />
                <div style={{ position: "absolute", left: -2, top: 170, width: 3, height: 52, borderRadius: "3px 0 0 3px", background: "linear-gradient(#626264,#2c2c2e)" }} />
                <div style={{ position: "absolute", left: -2, top: 236, width: 3, height: 52, borderRadius: "3px 0 0 3px", background: "linear-gradient(#626264,#2c2c2e)" }} />
                <div style={{ position: "absolute", right: -2, top: 198, width: 3, height: 84, borderRadius: "0 3px 3px 0", background: "linear-gradient(#626264,#2c2c2e)" }} />
                <div style={{ position: "relative", borderRadius: 53, background: "#000", padding: 6 }}>
                  <div style={{ position: "relative", borderRadius: 42, overflow: "hidden", background: "#F3F5F1", height: 548 }}>
                    {/* Status bar */}
                    <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 48, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 26px 0", zIndex: 8, fontFamily: "var(--font-sans), sans-serif", pointerEvents: "none" }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: "#10211B", letterSpacing: "0.01em" }}>9.41</span>
                      <span style={{ display: "flex", alignItems: "center", gap: 6, color: "#10211B" }}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="1" y="14" width="3" height="6" rx="1"/><rect x="6" y="10" width="3" height="10" rx="1"/><rect x="11" y="6" width="3" height="14" rx="1"/><rect x="16" y="2" width="3" height="18" rx="1"/></svg>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12.55a11 11 0 0 1 14 0"/><path d="M1.42 9a16 16 0 0 1 21.16 0"/><path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><circle cx="12" cy="20" r="0.5" fill="currentColor"/></svg>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 1.5 }}>
                          <span style={{ width: 22, height: 11, border: "1px solid rgba(16,33,27,0.45)", borderRadius: 3, padding: 1.5, display: "inline-flex" }}><span style={{ flex: 1, background: "#10211B", borderRadius: 1 }} /></span>
                          <span style={{ width: 1.5, height: 4, background: "rgba(16,33,27,0.45)", borderRadius: "0 1px 1px 0" }} />
                        </span>
                      </span>
                    </div>
                    {/* Dynamic Island */}
                    <div style={{ position: "absolute", top: 13, left: "50%", transform: "translateX(-50%)", width: 80, height: 16, background: "#000", borderRadius: 16, zIndex: 9, display: "flex", alignItems: "center", justifyContent: "flex-end", paddingRight: 10 }}>
                      <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#0a0a0c", boxShadow: "inset 0 0 2px rgba(90,130,170,0.65)" }} />
                    </div>

                    {/* Screen 1 — request form */}
                    <div className="offb-form" style={{ position: "absolute", inset: 0, padding: "50px 20px 18px", background: "#F3F5F1" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 7, background: "#fff", border: "1px solid #DCE3DD", borderRadius: 10, padding: "8px 11px", marginBottom: 18 }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#2F6B48" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
                        <span style={{ fontFamily: "var(--font-mono), monospace", fontSize: 10.5, color: "#16312A" }}>anna-el · {illLang === 'da' ? 'ny anmodning' : 'new request'}</span>
                      </div>
                      <div style={{ fontFamily: "var(--font-serif), serif", fontSize: 21, fontWeight: 600, marginBottom: 18, padding: "0 2px" }}>{illLang === 'da' ? 'Beskriv din opgave' : 'Describe your job'}</div>
                      <div className="offb-f1" style={{ marginBottom: 13 }}>
                        <div style={{ fontFamily: "var(--font-mono), monospace", fontSize: 9.5, letterSpacing: "0.1em", textTransform: "uppercase", color: "#597066", marginBottom: 5 }}>{illLang === 'da' ? 'Hvad skal laves?' : 'What needs doing?'}</div>
                        <div style={{ background: "#fff", border: "1px solid #C7D2CC", borderRadius: 10, padding: "11px 12px", fontSize: 13, fontWeight: 500 }}>{illLang === 'da' ? 'Udskift 6 stikkontakter i køkkenet' : 'Replace 6 sockets in the kitchen'}</div>
                      </div>
                      <div className="offb-f2" style={{ marginBottom: 13 }}>
                        <div style={{ fontFamily: "var(--font-mono), monospace", fontSize: 9.5, letterSpacing: "0.1em", textTransform: "uppercase", color: "#597066", marginBottom: 5 }}>{illLang === 'da' ? 'Hvornår?' : 'When?'}</div>
                        <div style={{ background: "#fff", border: "1px solid #C7D2CC", borderRadius: 10, padding: "11px 12px", fontSize: 13, fontWeight: 500 }}>{illLang === 'da' ? 'Inden for 2 uger' : 'Within 2 weeks'}</div>
                      </div>
                      <div className="offb-f3" style={{ marginBottom: 18 }}>
                        <div style={{ fontFamily: "var(--font-mono), monospace", fontSize: 9.5, letterSpacing: "0.1em", textTransform: "uppercase", color: "#597066", marginBottom: 5 }}>{illLang === 'da' ? 'Adresse' : 'Address'}</div>
                        <div style={{ background: "#fff", border: "1px solid #C7D2CC", borderRadius: 10, padding: "11px 12px", fontSize: 13, fontWeight: 500 }}>Bredgade 8, Kbh N</div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 7, background: "#F97316", color: "#fff", borderRadius: 11, padding: 13, fontSize: 13.5, fontWeight: 600 }}>
                        {illLang === 'da' ? 'Send anmodning' : 'Send request'}
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
                      </div>
                    </div>

                    {/* Screen 2 — calculating */}
                    <div className="offb-calc" style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, background: "#F3F5F1", textAlign: "center", padding: 30 }}>
                      <svg className="offb-spin" width="30" height="30" viewBox="0 0 24 24" fill="#2F6B48" aria-hidden="true"><path d="M12 2l1.6 4.9L18.5 8l-4.9 1.1L12 14l-1.6-4.9L5.5 8l4.9-1.1L12 2zm6 11l.9 2.6L21.5 16l-2.6.4L18 19l-.9-2.6L14.5 16l2.6-.4L18 13zM6 14l.7 2L9 16.7 7 17.4 6 19.5 5 17.4 3 16.7 5 16 6 14z"/></svg>
                      <div style={{ fontFamily: "var(--font-serif), serif", fontSize: 20, fontWeight: 600, color: "#16312A" }}>{illLang === 'da' ? 'Beregner tilbud…' : 'Calculating quote…'}</div>
                      <div style={{ fontSize: 12.5, color: "#597066", maxWidth: 200 }}>{illLang === 'da' ? 'Svend AI finder pris, ledig tid og beskriver opgaven' : 'Svend AI finds price, a free slot and describes the job'}</div>
                    </div>

                    {/* Screen 3 — finished offer */}
                    <div className="offb-offer" style={{ position: "absolute", inset: 0, padding: "50px 18px 18px", background: "#F3F5F1", overflow: "hidden" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                        <span style={{ fontFamily: "var(--font-serif), serif", fontSize: 19, fontWeight: 600 }}>{illLang === 'da' ? 'Dit tilbud' : 'Your quote'}</span>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10, fontWeight: 600, color: "#2F6B48", background: "#E0ECE7", padding: "4px 9px", borderRadius: 999 }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M8 12l3 3 5-6"/></svg>
                          {illLang === 'da' ? 'Klar' : 'Ready'}
                        </span>
                      </div>
                      <div style={{ background: "#fff", border: "1px solid #DCE3DD", borderRadius: 14, padding: 16, marginBottom: 11 }}>
                        <div style={{ fontFamily: "var(--font-mono), monospace", fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase", color: "#597066", marginBottom: 6 }}>{illLang === 'da' ? 'Estimeret pris' : 'Estimated price'}</div>
                        <div style={{ fontFamily: "var(--font-serif), serif", fontSize: 34, fontWeight: 600, color: "#C2410C", lineHeight: 1 }}>kr 4.850</div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, background: "#fff", border: "1px solid #DCE3DD", borderRadius: 12, padding: 12, marginBottom: 11 }}>
                        <span style={{ width: 32, height: 32, borderRadius: 9, background: "#E0ECE7", display: "flex", alignItems: "center", justifyContent: "center", color: "#2F6B48", flex: "0 0 auto" }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
                        </span>
                        <div>
                          <div style={{ fontSize: 9, fontFamily: "var(--font-mono), monospace", letterSpacing: "0.1em", textTransform: "uppercase", color: "#597066" }}>{illLang === 'da' ? 'Foreslået tid' : 'Suggested time'}</div>
                          <div style={{ fontSize: 12.5, fontWeight: 600 }}>Tir 18. jun · 08–12</div>
                        </div>
                      </div>
                      <div style={{ background: "#fff", border: "1px solid #DCE3DD", borderRadius: 12, padding: 12 }}>
                        <div style={{ fontSize: 9, fontFamily: "var(--font-mono), monospace", letterSpacing: "0.1em", textTransform: "uppercase", color: "#597066", marginBottom: 5 }}>{illLang === 'da' ? 'Opgavebeskrivelse' : 'Job description'}</div>
                        <div style={{ fontSize: 11.5, lineHeight: 1.5, color: "#3c4942" }}>{illLang === 'da' ? 'Demontering af 6 stikkontakter. Montering af nye jordede stik inkl. afprøvning og dokumentation.' : 'Removal of 6 sockets. Fitting of new earthed sockets incl. testing and documentation.'}</div>
                      </div>
                    </div>
                    {/* Home indicator */}
                    <div style={{ position: "absolute", bottom: 8, left: "50%", transform: "translateX(-50%)", width: 120, height: 5, borderRadius: 3, background: "rgba(16,33,27,0.34)", zIndex: 8 }} />
                  </div>
                </div>
              </div>
            </div>

            {/* Callouts stacked under phone on small screens */}
            <div className="offer-callouts-stack" style={{ display: "none", flexDirection: "column", gap: 12, marginTop: 28, width: "100%", maxWidth: 340 }}>
              <div style={{ background: "#FFFFFF", border: "1px solid #DCE3DD", borderRadius: 14, padding: "13px 15px" }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#597066", marginBottom: 5 }}>{illLang === 'da' ? 'Pris beregnet automatisk' : 'Price calculated automatically'}</div>
                <div style={{ fontFamily: "var(--font-serif), serif", fontSize: 24, fontWeight: 600, color: "#C2410C", lineHeight: 1 }}>kr 4.850</div>
              </div>
              <div style={{ background: "#FFFFFF", border: "1px solid #DCE3DD", borderRadius: 14, padding: "13px 15px" }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#597066", marginBottom: 5 }}>{illLang === 'da' ? 'Ledig tid fundet' : 'Free slot found'}</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#10211B" }}>Tir 18. jun · 08–12</div>
              </div>
              <div style={{ background: "#FFFFFF", border: "1px solid #DCE3DD", borderRadius: 14, padding: "13px 15px" }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#597066", marginBottom: 5 }}>{illLang === 'da' ? 'Opgave beskrevet' : 'Job described'}</div>
                <div style={{ fontSize: 12.5, lineHeight: 1.45, color: "#3c4942" }}>{illLang === 'da' ? 'Demontering + montering af 6 jordede stikkontakter, inkl. test.' : 'Removal + fitting of 6 earthed sockets, incl. testing.'}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== SÅDAN VIRKER DET · 3 steps ===================== */}
      <section
        id="how"
        className="section-pad"
        style={{ background: "#FFFFFF", padding: "100px clamp(20px,6vw,64px)", fontFamily: "var(--font-sans), sans-serif" }}
      >
        <div style={{ maxWidth: 1180, margin: "0 auto" }}>
          <div style={{ maxWidth: 680, margin: "0 0 56px" }}>
            <div style={{ fontFamily: "var(--font-mono), monospace", fontSize: 11, fontWeight: 500, letterSpacing: "0.16em", textTransform: "uppercase", color: "#2F6B48", marginBottom: 16 }}>
              {illLang === 'da' ? 'Sådan virker det' : 'How it works'}
            </div>
            <h2 style={{ fontFamily: "var(--font-serif), serif", fontSize: "clamp(30px,4vw,46px)", lineHeight: 1.08, letterSpacing: "-0.02em", fontWeight: 600, margin: 0, color: "#10211B" }}>
              {illLang === 'da' ? 'Tre trin fra forespørgsel til færdigt tilbud' : 'Three steps from request to finished quote'}
            </h2>
          </div>
          <div className="how-steps-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 20 }}>
            <div style={{ background: "#F3F5F1", border: "1px solid #DCE3DD", borderRadius: 18, padding: 30 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 22 }}>
                <span style={{ fontFamily: "var(--font-serif), serif", fontSize: 15, fontWeight: 600, width: 34, height: 34, borderRadius: 10, background: "#16312A", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>1</span>
                <span style={{ width: 44, height: 44, borderRadius: 12, background: "#E0ECE7", display: "flex", alignItems: "center", justifyContent: "center", color: "#2F6B48" }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4"/></svg>
                </span>
              </div>
              <div style={{ fontFamily: "var(--font-serif), serif", fontSize: 21, fontWeight: 600, marginBottom: 10, color: "#10211B" }}>{illLang === 'da' ? 'Del dit link' : 'Share your link'}</div>
              <p style={{ fontSize: 14.5, lineHeight: 1.6, color: "#597066", margin: 0 }}>{illLang === 'da' ? 'Send dit personlige tilbudslink til kunden — på SMS, mail eller fra din hjemmeside. Ingen app, kunden skal hente.' : 'Send your personal quote link to the customer — by SMS, email or from your website. No app for the customer to download.'}</p>
            </div>
            <div style={{ background: "#F3F5F1", border: "1px solid #DCE3DD", borderRadius: 18, padding: 30 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 22 }}>
                <span style={{ fontFamily: "var(--font-serif), serif", fontSize: 15, fontWeight: 600, width: 34, height: 34, borderRadius: 10, background: "#16312A", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>2</span>
                <span style={{ width: 44, height: 44, borderRadius: 12, background: "#E0ECE7", display: "flex", alignItems: "center", justifyContent: "center", color: "#2F6B48" }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>
                </span>
              </div>
              <div style={{ fontFamily: "var(--font-serif), serif", fontSize: 21, fontWeight: 600, marginBottom: 10, color: "#10211B" }}>{illLang === 'da' ? 'Kunden beskriver opgaven' : 'The customer describes the job'}</div>
              <p style={{ fontSize: 14.5, lineHeight: 1.6, color: "#597066", margin: 0 }}>{illLang === 'da' ? 'Kunden skriver kort hvad der skal laves og hvornår. Svendetid stiller de rigtige spørgsmål, så du får alt med fra start.' : 'The customer briefly writes what needs doing and when. Svendetid asks the right questions, so you get everything from the start.'}</p>
            </div>
            <div style={{ background: "#16312A", border: "1px solid #16312A", borderRadius: 18, padding: 30 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 22 }}>
                <span style={{ fontFamily: "var(--font-serif), serif", fontSize: 15, fontWeight: 600, width: 34, height: 34, borderRadius: 10, background: "#F97316", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>3</span>
                <span style={{ width: 44, height: 44, borderRadius: 12, background: "rgba(244,151,76,0.16)", display: "flex", alignItems: "center", justifyContent: "center", color: "#F4974C" }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2l1.6 4.9L18.5 8l-4.9 1.1L12 14l-1.6-4.9L5.5 8l4.9-1.1L12 2zm6 11l.9 2.6L21.5 16l-2.6.4L18 19l-.9-2.6L14.5 16l2.6-.4L18 13zM6 14l.7 2L9 16.7 7 17.4 6 19.5 5 17.4 3 16.7 5 16 6 14z"/></svg>
                </span>
              </div>
              <div style={{ fontFamily: "var(--font-serif), serif", fontSize: 21, fontWeight: 600, marginBottom: 10, color: "#fff" }}>{illLang === 'da' ? 'Få et færdigt tilbud' : 'Get a finished quote'}</div>
              <p style={{ fontSize: 14.5, lineHeight: 1.6, color: "#B7C9C0", margin: 0 }}>{illLang === 'da' ? 'Pris, ledig tid og en fuld opgavebeskrivelse ligger klar — du gennemser, justerer og sender med ét tryk.' : 'Price, a free slot and a full job description are ready — you review, adjust and send with one tap.'}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== CTA ===================== */}
      <section
        className="section-pad"
        style={{ background: "#FFFFFF", padding: "100px clamp(20px,6vw,64px)",  fontFamily: "var(--font-sans), sans-serif" }}
      >
        <div style={{ maxWidth: 980, margin: "0 auto", background: "#0F1513", borderRadius: 28, padding: "clamp(40px,6vw,72px)", textAlign: "center", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", width: 360, height: 360, borderRadius: "50%", background: "rgba(46,125,79,0.22)", filter: "blur(80px)", top: -120, left: -60, pointerEvents: "none" }} />
          <div style={{ position: "absolute", width: 300, height: 300, borderRadius: "50%", background: "rgba(249,115,22,0.16)", filter: "blur(80px)", bottom: -120, right: -40, pointerEvents: "none" }} />
          <div style={{ position: "relative" }}>
            <h2 style={{ fontFamily: "var(--font-serif), serif", fontSize: "clamp(32px,4.4vw,52px)", lineHeight: 1.06, letterSpacing: "-0.02em", fontWeight: 600, color: "#fff", margin: "0 0 18px" }}>
              {illLang === 'da' ? 'Stop med at skrive tilbud i hånden' : 'Stop writing quotes by hand'}
            </h2>
            <p style={{ fontSize: 18, lineHeight: 1.6, color: "#B7C9C0", maxWidth: 560, margin: "0 auto 34px" }}>
              {illLang === 'da' ? 'Lad kunderne beskrive opgaven, og lad Svendetid gøre resten. Prøv automatiske tilbud gratis i 14 dage.' : 'Let customers describe the job, and let Svendetid do the rest. Try automatic quotes free for 14 days.'}
            </p>
            <div className="cta-btn-row" style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
              <SignUpButton mode="modal" fallbackRedirectUrl="/org/create">
                <button style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#F97316", color: "#fff", border: "none", fontSize: 16, padding: "16px 32px", borderRadius: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", boxShadow: "0 12px 28px rgba(194,65,12,0.34)" }}>
                  {illLang === 'da' ? 'Start gratis prøveperiode →' : 'Start free trial →'}
                </button>
              </SignUpButton>
              <a href="#how" style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "transparent", color: "#EEF4F0", textDecoration: "none", fontSize: 16, padding: "16px 28px", borderRadius: 12, fontWeight: 600, border: "1px solid #2C3B35" }}>
                {illLang === 'da' ? 'Se hvordan det virker' : 'See how it works'}
              </a>
            </div>
            <p style={{ fontSize: 13, color: "#9FB0A8", margin: "22px 0 0" }}>{illLang === 'da' ? '14 dages gratis · Intet kort · Opsig når som helst' : '14 days free · No card · Cancel anytime'}</p>
          </div>
        </div>
      </section>

      <LandingFooter lang={illLang} daHref="/" enHref="/en" />
      <CookieConsent lang={illLang} />
    </div>
  );
}
